import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Loading, PageHeader } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import CancelAlertPopup from '../../components/CancelAlertPopup/CancelAlertPopup';
import useComponentState from '../../utils/useComponentState';
import styles from './ManageRewardRules.module.scss';
import { Drawer } from 'rsuite';
import { toast } from 'react-toastify';
import {
  CONDITION_TYPES,
  ACTION_TYPES,
  CONDITION_OPERATORS,
} from '../AddRewardRule/AddRewardRuleConstants';
import {
  getAllRewardRules,
  updateRewardRule,
  deleteRewardRule,
  toggleRewardRule,
} from '../../api/rewardRule';
import { getAllLoyaltyTiers } from '../../api/tier';
import PermissionGate from '../../components/PermissionGate/PermissionGate';
import { usePermissions } from '../../hooks/usePermissions';
import { REWARD_RULE_MANAGEMENT_PERMISSIONS } from '../../constants/permissions';

const EMPTY_EDIT_FORM = {
  name: '',
  description: '',
  priority: '',
  isActive: true,
  startDate: '',
  endDate: '',
  conditionOperator: 'AND',
  conditions: [{ conditionType: '', value: '', secondaryValue: '' }],
  actionType: '',
  actionValue: '',
  mccCodes: '',
  selectedTierIds: [],
};

const formatDateTime = (dt) => {
  if (!dt) return null;
  return dt.length === 16 ? `${dt}:00` : dt;
};

const parseMccCodes = (mccStr) => {
  if (!mccStr || !mccStr.trim()) return [];
  return mccStr.split(',').map((s) => s.trim()).filter(Boolean);
};

const populateEditForm = (rule) => ({
  name: rule.name || '',
  description: rule.description || '',
  priority: rule.priority !== undefined ? String(rule.priority) : '',
  isActive: rule.isActive !== undefined ? rule.isActive : true,
  startDate: rule.startDate ? rule.startDate.substring(0, 16) : '',
  endDate: rule.endDate ? rule.endDate.substring(0, 16) : '',
  conditionOperator: rule.conditionOperator || 'AND',
  conditions:
    rule.conditions?.length > 0
      ? rule.conditions.map((c) => ({
          conditionType: c.conditionType || '',
          value: c.value !== undefined ? String(c.value) : '',
          secondaryValue:
            c.secondaryValue !== null && c.secondaryValue !== undefined
              ? String(c.secondaryValue)
              : '',
        }))
      : [{ conditionType: '', value: '', secondaryValue: '' }],
  actionType: rule.action?.actionType || '',
  actionValue:
    rule.action?.actionValue !== null && rule.action?.actionValue !== undefined
      ? String(rule.action.actionValue)
      : '',
  mccCodes: rule.mccCodes?.join(', ') || '',
  selectedTierIds: rule.tierScopes?.map((ts) => ts.loyaltyTierId) || [],
});

const validateEditForm = (form) => {
  const errors = {};
  const conditionErrors = [];

  if (!form.name || !form.name.trim()) {
    errors.name = 'Rule name is required.';
  }

  const priority = parseInt(form.priority, 10);
  if (!form.priority || isNaN(priority) || priority < 1) {
    errors.priority = 'Priority is required and must be a positive integer.';
  }

  if (!form.actionType) {
    errors.actionType = 'Action type is required.';
  }

  const actionTypeDef = ACTION_TYPES.find((a) => a.id === form.actionType);
  if (actionTypeDef?.needsValue && (form.actionValue === '' || form.actionValue === undefined)) {
    errors.actionValue = 'Action value is required for this action type.';
  }

  form.conditions.forEach((cond) => {
    const condErr = {};
    if (!cond.conditionType) {
      condErr.conditionType = 'Condition type is required.';
    }
    if (cond.value === '' || cond.value === undefined) {
      condErr.value = 'Value is required.';
    }
    const condTypeDef = CONDITION_TYPES.find((c) => c.id === cond.conditionType);
    if (condTypeDef?.needsSecondary) {
      const v = parseFloat(cond.value);
      const sv = parseFloat(cond.secondaryValue);
      if (cond.secondaryValue === '' || isNaN(sv)) {
        condErr.secondaryValue = 'Secondary value is required.';
      } else if (!isNaN(v) && sv <= v) {
        condErr.secondaryValue = 'Secondary value must be greater than the first value.';
      }
    }
    conditionErrors.push(condErr);
  });

  if (form.startDate && form.endDate) {
    if (new Date(form.endDate) <= new Date(form.startDate)) {
      errors.dateRange = 'End date must be after start date.';
    }
  }

  const hasConditionErrors = conditionErrors.some((e) => Object.keys(e).length > 0);

  return {
    isValid: Object.keys(errors).length === 0 && !hasConditionErrors,
    errors,
    conditionErrors,
  };
};

const buildUpdatePayload = (id, form) => {
  const payload = {
    id,
    name: form.name.trim(),
    description: form.description ? form.description.trim() : null,
    isActive: form.isActive,
    priority: parseInt(form.priority, 10),
    conditionOperator: form.conditionOperator,
    conditions: form.conditions.map((c) => ({
      conditionType: c.conditionType,
      value: parseFloat(c.value),
      secondaryValue: c.secondaryValue !== '' ? parseFloat(c.secondaryValue) : null,
    })),
    action: {
      actionType: form.actionType,
      actionValue: form.actionValue !== '' ? parseFloat(form.actionValue) : null,
    },
    mccCodes: parseMccCodes(form.mccCodes),
    tierIds: form.selectedTierIds,
  };
  if (form.startDate) payload.startDate = formatDateTime(form.startDate);
  if (form.endDate) payload.endDate = formatDateTime(form.endDate);
  return payload;
};

const formatDateDisplay = (dt) => {
  if (!dt) return '—';
  return dt.substring(0, 16).replace('T', ' ');
};

const getActionLabel = (actionType) =>
  ACTION_TYPES.find((a) => a.id === actionType)?.name || actionType || 'N/A';

const getConditionLabel = (conditionType) =>
  CONDITION_TYPES.find((c) => c.id === conditionType)?.name || conditionType || 'N/A';

const ManageRewardRules = ({ auth }) => {
  const { can } = usePermissions();
  const [state, setState] = useComponentState({
    tableHeading: {
      column: ['Rule Name', 'Priority', 'Condition Operator', 'Action Type', 'Active', 'Date Range'],
    },
    rulesData: [],
    loading: false,
    loadError: '',

    showDrawer: false,
    drawerData: null,

    editMode: false,
    editForm: EMPTY_EDIT_FORM,
    fieldErrors: {},
    conditionErrors: [],
    editSaving: false,

    showDeleteConfirm: false,
    ruleToDelete: null,

    tiers: [],
    tiersLoading: false,
  });

  useEffect(() => {
    loadRules();
    loadTiers();
    // eslint-disable-next-line
  }, []);

  const loadRules = async () => {
    setState({ loading: true, loadError: '' });
    try {
      const data = await getAllRewardRules(auth.auth.token);
      setState({ rulesData: Array.isArray(data) ? data : [], loading: false });
    } catch (err) {
      setState({ loadError: 'Failed to load reward rules. Please try again.', loading: false });
      toast.error(err?.message || 'Failed to load reward rules.');
    }
  };

  const loadTiers = async () => {
    setState({ tiersLoading: true });
    try {
      const data = await getAllLoyaltyTiers(auth.auth.token);
      setState({ tiers: Array.isArray(data) ? data : [], tiersLoading: false });
    } catch {
      setState({ tiers: [], tiersLoading: false });
    }
  };

  const handleDrawerOpen = (rowData) => {
    if (!rowData) return;
    setState({
      showDrawer: true,
      drawerData: rowData,
      editMode: false,
      editForm: populateEditForm(rowData),
      fieldErrors: {},
      conditionErrors: [],
    });
  };

  const handleCloseDrawer = () => {
    setState({ showDrawer: false, drawerData: null, editMode: false });
  };

  const handleEditFormChange = (field, value) => {
    setState((prev) => ({
      ...prev,
      editForm: { ...prev.editForm, [field]: value },
      fieldErrors: { ...prev.fieldErrors, [field]: '' },
    }));
  };

  const handleConditionChange = (idx, field, value) => {
    const updatedConditions = state.editForm.conditions.map((c, i) =>
      i === idx
        ? { ...c, [field]: value, ...(field === 'conditionType' ? { value: '', secondaryValue: '' } : {}) }
        : c
    );
    const updatedErrors = [...(state.conditionErrors || [])];
    if (updatedErrors[idx]) {
      updatedErrors[idx] = { ...updatedErrors[idx], [field]: '' };
    }
    setState({
      editForm: { ...state.editForm, conditions: updatedConditions },
      conditionErrors: updatedErrors,
    });
  };

  const handleAddCondition = () => {
    setState({
      editForm: {
        ...state.editForm,
        conditions: [
          ...state.editForm.conditions,
          { conditionType: '', value: '', secondaryValue: '' },
        ],
      },
    });
  };

  const handleRemoveCondition = (idx) => {
    if (state.editForm.conditions.length === 1) return;
    setState({
      editForm: {
        ...state.editForm,
        conditions: state.editForm.conditions.filter((_, i) => i !== idx),
      },
      conditionErrors: (state.conditionErrors || []).filter((_, i) => i !== idx),
    });
  };

  const handleTierToggle = (tierId) => {
    const selected = state.editForm.selectedTierIds;
    const updated = selected.includes(tierId)
      ? selected.filter((id) => id !== tierId)
      : [...selected, tierId];
    setState({ editForm: { ...state.editForm, selectedTierIds: updated } });
  };

  const handleSave = async () => {
    if (!can(REWARD_RULE_MANAGEMENT_PERMISSIONS.MANAGE_RULES.UPDATE)) {
      toast.error('You are not authorized to perform this action.');
      return;
    }
    const { isValid, errors, conditionErrors } = validateEditForm(state.editForm);
    if (!isValid) {
      setState({ fieldErrors: errors, conditionErrors });
      return;
    }

    setState({ editSaving: true });
    try {
      const payload = buildUpdatePayload(state.drawerData.id, state.editForm);
      const updated = await updateRewardRule(payload, auth.auth.token);
      const updatedList = state.rulesData.map((r) => (r.id === updated.id ? updated : r));
      setState({
        rulesData: updatedList,
        drawerData: updated,
        editMode: false,
        editSaving: false,
        fieldErrors: {},
        conditionErrors: [],
      });
      toast.success('Reward rule updated successfully.');
    } catch (err) {
      setState({ editSaving: false });
      toast.error(err?.message || 'Failed to update reward rule.');
    }
  };

  const handleDeleteClick = (ruleData) => {
    setState({ showDeleteConfirm: true, ruleToDelete: ruleData });
  };

  const handleConfirmDelete = async () => {
    const rule = state.ruleToDelete;
    if (!rule) return;
    if (!can(REWARD_RULE_MANAGEMENT_PERMISSIONS.MANAGE_RULES.DELETE)) {
      toast.error('You are not authorized to perform this action.');
      setState({ showDeleteConfirm: false, ruleToDelete: null });
      return;
    }
    setState({ loading: true, showDeleteConfirm: false });
    try {
      await deleteRewardRule(rule.id, auth.auth.token);
      setState({
        rulesData: state.rulesData.filter((r) => r.id !== rule.id),
        showDrawer: false,
        drawerData: null,
        loading: false,
        ruleToDelete: null,
      });
      toast.success('Reward rule deleted.');
    } catch (err) {
      setState({ loading: false, ruleToDelete: null });
      toast.error(err?.message || 'Failed to delete reward rule.');
    }
  };

  const handleToggleStatus = async (ruleData) => {
    if (!can(REWARD_RULE_MANAGEMENT_PERMISSIONS.MANAGE_RULES.ACTIVATE_RULE)) {
      toast.error('You are not authorized to perform this action.');
      return;
    }
    try {
      const updated = await toggleRewardRule(ruleData.id, auth.auth.token);
      const updatedList = state.rulesData.map((r) => (r.id === updated.id ? updated : r));
      setState({ rulesData: updatedList, drawerData: updated });
      toast.success(`Rule ${updated.isActive ? 'activated' : 'deactivated'}.`);
    } catch (err) {
      toast.error(err?.message || 'Failed to toggle rule status.');
    }
  };

  const renderViewMode = () => {
    const data = state.drawerData;
    if (!data) return null;

    const dateRange =
      data.startDate || data.endDate
        ? `${formatDateDisplay(data.startDate)} → ${formatDateDisplay(data.endDate)}`
        : 'Always active';

    return (
      <>
        <div className={styles.detail_row}>
          <strong>Rule Name:</strong>
          <span>{data.name || 'N/A'}</span>
        </div>
        <div className={styles.detail_row}>
          <strong>Description:</strong>
          <span>{data.description || '—'}</span>
        </div>
        <div className={styles.detail_row}>
          <strong>Priority:</strong>
          <span>{data.priority}</span>
        </div>
        <div className={styles.detail_row}>
          <strong>Status:</strong>
          <span className={data.isActive ? styles.badge_active : styles.badge_inactive}>
            {data.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className={styles.detail_row}>
          <strong>Date Range:</strong>
          <span>{dateRange}</span>
        </div>
        <div className={styles.detail_row}>
          <strong>Condition Operator:</strong>
          <span>{CONDITION_OPERATORS.find((o) => o.id === data.conditionOperator)?.name || data.conditionOperator}</span>
        </div>
        <div className={styles.detail_row}>
          <strong>Conditions:</strong>
          <div className={styles.conditions_list}>
            {data.conditions?.length > 0 ? (
              data.conditions.map((c, i) => (
                <span key={i} className={styles.condition_item}>
                  {getConditionLabel(c.conditionType)}{': '}
                  {c.secondaryValue !== null && c.secondaryValue !== undefined
                    ? `${c.value} – ${c.secondaryValue}`
                    : c.value}
                </span>
              ))
            ) : (
              <span>No conditions</span>
            )}
          </div>
        </div>
        <div className={styles.detail_row}>
          <strong>Action:</strong>
          <span>
            {getActionLabel(data.action?.actionType)}
            {data.action?.actionValue !== null && data.action?.actionValue !== undefined
              ? ` ${data.action.actionValue}`
              : ''}
          </span>
        </div>
        <div className={styles.detail_row}>
          <strong>MCC Codes:</strong>
          <span>{data.mccCodes?.length > 0 ? data.mccCodes.join(', ') : 'All MCCs'}</span>
        </div>
        <div className={styles.detail_row}>
          <strong>Tier Scope:</strong>
          <span>
            {data.tierScopes?.length > 0
              ? data.tierScopes.map((ts) => ts.loyaltyTierName).join(', ')
              : 'All tiers'}
          </span>
        </div>
        <div className={styles.detail_row}>
          <strong>Created:</strong>
          <span>{formatDateDisplay(data.createdDate)}</span>
        </div>
      </>
    );
  };

  const renderEditMode = () => {
    const form = state.editForm;
    const actionTypeDef = ACTION_TYPES.find((a) => a.id === form.actionType);

    return (
      <div className={styles.edit_form}>

        {/* Basic Information */}
        <div className={styles.edit_section}>
          <div className={styles.edit_section_title}>Basic Information</div>

          <div className={styles.field_row}>
            <label className={styles.form_label}>
              Rule Name <span className="text-danger">*</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                placeholder="Rule name"
                className={state.fieldErrors?.name ? styles.input_error : ''}
              />
              {state.fieldErrors?.name && (
                <span className={styles.error_message}>{state.fieldErrors.name}</span>
              )}
            </label>

            <label className={styles.form_label}>
              Priority <span className="text-danger">*</span>
              <input
                type="number"
                min="1"
                step="1"
                value={form.priority}
                onChange={(e) => handleEditFormChange('priority', e.target.value)}
                placeholder="1 = highest priority"
                className={state.fieldErrors?.priority ? styles.input_error : ''}
              />
              {state.fieldErrors?.priority && (
                <span className={styles.error_message}>{state.fieldErrors.priority}</span>
              )}
            </label>
          </div>

          <label className={styles.form_label}>
            Description
            <textarea
              value={form.description}
              onChange={(e) => handleEditFormChange('description', e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </label>

          <div
            className={styles.active_row}
            onClick={() => handleEditFormChange('isActive', !form.isActive)}
          >
            <input
              type="checkbox"
              id="edit_isActive"
              checked={form.isActive}
              onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
            />
            <label htmlFor="edit_isActive" className={styles.active_row_label}>
              Rule is {form.isActive ? <strong>active</strong> : <span style={{ color: '#718096' }}>inactive</span>}
            </label>
          </div>
        </div>

        {/* Date Range */}
        <div className={styles.edit_section}>
          <div className={styles.edit_section_title}>
            Date Range <span className={styles.optional_badge}>Optional</span>
          </div>

          <div className={styles.field_row}>
            <label className={styles.form_label}>
              Start Date
              <input
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => handleEditFormChange('startDate', e.target.value)}
              />
            </label>

            <label className={styles.form_label}>
              End Date
              <input
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => handleEditFormChange('endDate', e.target.value)}
                className={state.fieldErrors?.dateRange ? styles.input_error : ''}
              />
              {state.fieldErrors?.dateRange && (
                <span className={styles.error_message}>{state.fieldErrors.dateRange}</span>
              )}
            </label>
          </div>
        </div>

        {/* Condition Operator */}
        <div className={styles.edit_section}>
          <div className={styles.edit_section_title}>Condition Operator</div>

          <div className={styles.operator_toggle_group}>
            {CONDITION_OPERATORS.map((op) => (
              <button
                key={op.id}
                type="button"
                className={`${styles.operator_btn} ${form.conditionOperator === op.id ? styles.operator_btn_active : ''}`}
                onClick={() => handleEditFormChange('conditionOperator', op.id)}
              >
                <strong>{op.id}</strong>&nbsp;&nbsp;{op.id === 'AND' ? 'All must match' : 'Any must match'}
              </button>
            ))}
          </div>
          <div className={styles.field_hint}>
            {form.conditionOperator === 'AND'
              ? 'The rule applies only when all conditions are satisfied.'
              : 'The rule applies when at least one condition is satisfied.'}
          </div>
        </div>

        {/* Conditions */}
        <div className={styles.edit_section}>
          <div className={styles.edit_section_title}>Conditions</div>

          {form.conditions.map((cond, idx) => {
            const condTypeDef = CONDITION_TYPES.find((c) => c.id === cond.conditionType);
            const condErr = (state.conditionErrors || [])[idx] || {};
            return (
              <div key={idx} className={styles.cond_card}>
                <div className={styles.cond_card_header}>
                  <span className={styles.cond_card_badge}>{idx + 1}</span>
                  <span className={styles.cond_card_title}>Condition {idx + 1}</span>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleRemoveCondition(idx)}
                    disabled={form.conditions.length === 1}
                  >
                    Remove
                  </button>
                </div>

                <div className={styles.cond_card_body}>
                  <div className={styles.cond_field} style={{ flex: '2', minWidth: '170px' }}>
                    <label>Condition Type <span className="text-danger">*</span></label>
                    <select
                      value={cond.conditionType}
                      onChange={(e) => handleConditionChange(idx, 'conditionType', e.target.value)}
                      className={condErr.conditionType ? styles.input_error : ''}
                    >
                      <option value="">Select type</option>
                      {CONDITION_TYPES.map((ct) => (
                        <option key={ct.id} value={ct.id}>{ct.name}</option>
                      ))}
                    </select>
                    {condErr.conditionType && (
                      <span className={styles.error_message}>{condErr.conditionType}</span>
                    )}
                  </div>

                  <div className={styles.cond_field} style={{ flex: '1' }}>
                    <label>
                      {condTypeDef?.needsSecondary ? 'From Value' : 'Value'}{' '}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 100"
                      value={cond.value}
                      step="any"
                      onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                      className={condErr.value ? styles.input_error : ''}
                    />
                    {condErr.value && <span className={styles.error_message}>{condErr.value}</span>}
                  </div>

                  {condTypeDef?.needsSecondary && (
                    <>
                      <div className={styles.cond_separator}>to</div>
                      <div className={styles.cond_field} style={{ flex: '1' }}>
                        <label>Max Value <span className="text-danger">*</span></label>
                        <input
                          type="number"
                          placeholder="e.g. 500"
                          value={cond.secondaryValue}
                          step="any"
                          onChange={(e) => handleConditionChange(idx, 'secondaryValue', e.target.value)}
                          className={condErr.secondaryValue ? styles.input_error : ''}
                        />
                        {condErr.secondaryValue && (
                          <span className={styles.error_message}>{condErr.secondaryValue}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <button
            type="button"
            className={`btn btn-outline-primary btn-sm ${styles.add_cond_btn}`}
            onClick={handleAddCondition}
          >
            + Add Condition
          </button>
        </div>

        {/* Action */}
        <div className={styles.edit_section}>
          <div className={styles.edit_section_title}>Action</div>

          <div className={styles.field_row}>
            <label className={styles.form_label}>
              Action Type <span className="text-danger">*</span>
              <select
                value={form.actionType}
                onChange={(e) => handleEditFormChange('actionType', e.target.value)}
                className={state.fieldErrors?.actionType ? styles.input_error : ''}
              >
                <option value="">Select action type</option>
                {ACTION_TYPES.map((at) => (
                  <option key={at.id} value={at.id}>{at.name}</option>
                ))}
              </select>
              {state.fieldErrors?.actionType && (
                <span className={styles.error_message}>{state.fieldErrors.actionType}</span>
              )}
            </label>

            {actionTypeDef?.needsValue && (
              <label className={styles.form_label}>
                Action Value <span className="text-danger">*</span>
                <input
                  type="number"
                  placeholder="e.g. 2.0"
                  step="any"
                  min="0"
                  value={form.actionValue}
                  onChange={(e) => handleEditFormChange('actionValue', e.target.value)}
                  className={state.fieldErrors?.actionValue ? styles.input_error : ''}
                />
                {state.fieldErrors?.actionValue && (
                  <span className={styles.error_message}>{state.fieldErrors.actionValue}</span>
                )}
              </label>
            )}
          </div>
        </div>

        {/* MCC Scope */}
        <div className={styles.edit_section}>
          <div className={styles.edit_section_title}>
            MCC Scope <span className={styles.optional_badge}>Optional</span>
          </div>
          <label className={styles.form_label}>
            MCC Codes
            <input
              type="text"
              value={form.mccCodes}
              onChange={(e) => handleEditFormChange('mccCodes', e.target.value)}
              placeholder="e.g. 1111, 2222"
            />
          </label>
          <div className={styles.field_hint}>Leave empty to apply to all MCC codes.</div>
        </div>

        {/* Tier Scope */}
        <div className={styles.edit_section}>
          <div className={styles.edit_section_title}>
            Tier Scope <span className={styles.optional_badge}>Optional</span>
          </div>
          {state.tiers.length > 0 ? (
            <div className={styles.tier_checkbox_group}>
              {state.tiers.map((tier) => (
                <label
                  key={tier.id}
                  className={form.selectedTierIds.includes(tier.id) ? styles.tier_selected : ''}
                >
                  <input
                    type="checkbox"
                    checked={form.selectedTierIds.includes(tier.id)}
                    onChange={() => handleTierToggle(tier.id)}
                  />
                  {tier.name}
                </label>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '13px', color: '#718096' }}>No tiers available.</span>
          )}
          <div className={styles.field_hint}>Leave unchecked to apply to all tiers.</div>
        </div>

      </div>
    );
  };

  const renderDrawer = () => {
    if (!state.drawerData) return null;
    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title>
              {state.editMode ? 'Edit Reward Rule' : 'Rule Details'}
            </Drawer.Title>
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.drawer_body}>
            {state.editMode ? renderEditMode() : renderViewMode()}
          </div>

          <div className={styles.drawer_actions}>
            {!state.editMode ? (
              <>
                <PermissionGate permission={REWARD_RULE_MANAGEMENT_PERMISSIONS.MANAGE_RULES.UPDATE}>
                  <button
                    type="button"
                    className={`btn btn-primary ${styles.action_btn}`}
                    onClick={() => setState({ editMode: true })}
                  >
                    Edit
                  </button>
                </PermissionGate>
                <PermissionGate permission={REWARD_RULE_MANAGEMENT_PERMISSIONS.MANAGE_RULES.ACTIVATE_RULE}>
                  <button
                    type="button"
                    className={`btn btn-outline-secondary ${styles.action_btn}`}
                    onClick={() => handleToggleStatus(state.drawerData)}
                  >
                    {state.drawerData.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </PermissionGate>
                <PermissionGate permission={REWARD_RULE_MANAGEMENT_PERMISSIONS.MANAGE_RULES.DELETE}>
                  <button
                    type="button"
                    className={`btn btn-danger ${styles.action_btn}`}
                    onClick={() => handleDeleteClick(state.drawerData)}
                  >
                    Delete
                  </button>
                </PermissionGate>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`btn btn-secondary ${styles.action_btn}`}
                  onClick={() => setState({ editMode: false, fieldErrors: {}, conditionErrors: [] })}
                  disabled={state.editSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-primary ${styles.action_btn}`}
                  onClick={handleSave}
                  disabled={state.editSaving}
                >
                  {state.editSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </Drawer.Body>
      </>
    );
  };

  return (
    <div className={styles.root_div}>
      {state.loading ? (
        <Loading text="Loading" />
      ) : (
        <div>
          <PageHeader text="Manage Reward Rules" />
          {state.loadError ? (
            <p>{state.loadError}</p>
          ) : (
            <div>
              <div className={styles.filter_btn_group} />
              <DataTable
                tableData={state.rulesData}
                tableheading={state.tableHeading}
                handleOnClick={handleDrawerOpen}
                tag="rewardRules"
                fetchError=""
                searchFilterLoading={false}
              />
            </div>
          )}
        </div>
      )}

      <CancelAlertPopup
        show={state.showDeleteConfirm}
        alertMessage={`Are you sure you want to delete the rule "${state.ruleToDelete?.name}"? This action cannot be undone.`}
        handleYesBtnClick={handleConfirmDelete}
        handleNoBtnClick={() => setState({ showDeleteConfirm: false, ruleToDelete: null })}
      />

      <Drawer show={state.showDrawer} onHide={handleCloseDrawer} placement="right">
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });
export default connect(mapStateToProps)(ManageRewardRules);
